import React, { useEffect, useState } from 'react';
import {
  lighten,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { t } from 'i18next';
import _ from 'lodash';
import { makeStyles } from 'tss-react/mui';
import { InteractionHistory } from '../contact-management.types';
import InteractionHistoryItem from './interaction-history-item';

interface Props {
  interactionHistories: InteractionHistory[];
}

const ContactInteractionHistory: React.FC<Props> = ({ interactionHistories }) => {
  const { classes } = useStyles();
  const [openHistory, setOpenHistory] = useState<number[]>([]);

  // Open all histories on first render
  useEffect(() => {
    setOpenHistory(Array.from({ length: interactionHistories.length }, (_item, index) => index));
  }, [interactionHistories]);

  const handleToggleIsOpen = (idx: number) => {
    return () => {
      setOpenHistory((prev) => {
        const updated = [...prev];
        if (updated.includes(idx)) {
          _.pull(updated, idx);
        } else {
          updated.push(idx);
        }
        return updated;
      });
    };
  };

  return (
    <TableContainer className={classes.container}>
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow className={classes.tableHeadRow}>
            <TableCell style={{ width: '50%' }}>{t('campaign')}</TableCell>
            <TableCell style={{ width: '50%' }}>{t('createDate')}</TableCell>
            <TableCell style={{ width: '45px' }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {!_.isEmpty(interactionHistories) ? (
            interactionHistories.map((item, index) => (
              <InteractionHistoryItem
                key={`${item.campaignName}-${index}`}
                interactionHistory={item}
                isOpen={openHistory.includes(index)}
                isEven={index % 2 === 0}
                isLast={index === interactionHistories.length - 1}
                handleToggleIsOpen={handleToggleIsOpen(index)}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} style={{ textAlign: 'center' }}>
                {t('noCampaignsFound')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    borderRadius: '12px',
    background: '#FFF',
    overflow: 'hidden'
  },
  table: {
    '& th': {
      border: 'none',
      padding: '4px 24px',
      fontSize: theme.typography.pxToRem(13),
      '&:first-of-type': {
        borderTopLeftRadius: '6px',
        borderBottomLeftRadius: '6px'
      },
      '&:last-of-type': {
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px'
      }
    },
    '& td, th': {
      border: 'none',
      borderBottom: '1px solid #ddd',
      padding: '8px 24px',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13)
    },
    '& mark': {
      backgroundColor: lighten(theme.palette.secondary.main, 0.8)
    }
  },
  tableHeadRow: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFF',
    zIndex: 9
  }
}));

export default ContactInteractionHistory;
